import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {setErrors, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import DropdownInput from "../../../components/DropdownInput";
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import {WithdrawCountryResponse} from "../../../types/Responses/WithdrawsResponse";
import CountryAvatar from "../../../components/CountryAvatar";
import {CountryShort} from "../../../types/Responses/CountriesResponse";
import {APILink} from "../../../types/APILink";

interface SkrillFormProps {
    broker: number,
    clientTransactionID?: number
}

interface CurrencyType extends FooterListItem {
    cashier_id: number;
}

const SkrillForm = ({broker, clientTransactionID}: SkrillFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [transaction, setTransaction] = useState<string>();
    const [currency, setCurrency] = useState<string>();
    const [currencyID, setCurrencyID] = useState<number>();
    const [accountCurrency, setAccountCurrency] = useState<number>();
    const [message, setMessage] = useState<string>('');
    const [country, setCountry] = useState<number>();

    const [currenciesList, setCurrenciesList] = useState<CurrencyType[]>();
    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [countriesList, setCountriesList] = useState<WithdrawCountryResponse>();

    const dispatch = useDispatch();

    const clearData = () => {
        setInProgress(false);
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setTransaction('');
        setCurrency('');
        setValidated(false)
    }

    const getCountries = useCallback(() => {
        return new Promise((resolve, reject) => {
            API.getWithdrawCountries(broker, WithdrawProvider.SKRILL).then((response: AxiosResponse) => {
                setCountriesList(response.data as WithdrawCountryResponse);
                resolve(undefined);
            }).catch(e => {
                ErrorHandler(e, `get${APILink.GET_WITHDRAW_COUNTRIES}`, toast, handleError);
                reject(undefined);
            });
        });
    }, [toast, handleError]);

    const getCurrencyAccount = useCallback(() => {
        return new Promise((resolve, reject) => {
            API.getSkrillCurrencies(broker).then((response: AxiosResponse) => {
                setCurrenciesList(response.data);
                resolve(undefined)
            }).catch(e => {
                ErrorHandler(e, `get${APILink.GET_SKRILL_CURRENCIES.replace('$broker', broker.toString())}`, toast, handleError);
                reject(undefined);
            });
        });
    }, [toast, handleError]);

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.client.first_name);
            setLastName(response.data.client.last_name);
            setEmail(response.data.client.email);
            setCountry(response.data.country.id);
            setCurrency(response.data.currency.code);
            if (response.data.request) {
                setAccountCurrency(response.data.request.account_id);
                setTransaction(response.data.request.mb_transaction_id);
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError]);

    const changeCountry = useCallback((u: string | number) => {
        setCountry(typeof u === "string" ? parseInt(u) : u);
        setValidated(false);
    }, [setCountry]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeCurrency = useCallback((s: string | number) => {
        if (!currenciesList)
            return;
        setValidated(false);
        const c = typeof s === "number" ? s : parseInt(s);
        setAccountCurrency(c);
        setCurrency(currenciesList.find(i => i.id === s)?.code);
        setCurrencyID(currenciesList.find(i => i.id === s)?.cashier_id);

    }, [setValidated, setAccountCurrency, setCurrency, currenciesList]);

    const changeMessage = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setMessage(e.currentTarget.value)
        }
    }, [setValidated, setMessage]);

    const changeTransaction = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setTransaction(e.currentTarget.value)
        }
    }, [setValidated, setTransaction]);

    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                country,
                amount,
                email,
                broker,
                message,
                account: accountCurrency,
                provider: WithdrawProvider.SKRILL,
                mb_transaction: transaction,
                first_name: firstName,
                last_name: lastName,
                currency: currencyID
            };
            API.makeSkrillWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    if (e instanceof AxiosError && e.response) {
                        setErrors(e.response.data.errors);
                    }
                    ErrorHandler(e, `post${APILink.MAKE_SKRILL_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [firstName, country, message, accountCurrency, currencyID, lastName, validated, amount, transaction, email, broker, clientTransactionID]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {key: 'country', label: t('admin.withdraws.country'), type: InputType.number, regex: '^\\d{1,3}$'},
            {key: 'currency', label: t('admin.withdraws.currency'), type: InputType.number, regex: '^\\d{1,3}$'},
            {key: 'account', label: t('admin.withdraws.currency'), type: InputType.number},
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'mb_transaction', label: t('admin.withdraws.transaction_id'), type: InputType.text, regex: '^.{6,}'}
        ];

        if (message.length) {
            inputs.push({key: 'message', label: t('admin.withdraws.message'), type: InputType.text, regex: '^.{5,}'});
        }

        const data = {
            country,
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            mb_transaction: transaction,
            account: accountCurrency,
            message,
            currency: currencyID
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, country, accountCurrency, message, currencyID, firstName, lastName, email, transaction, callback]);


    const amountUI = useMemo(() => {
        if (!currency)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency})} type="number" validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, changeAmount, amount, currency]);

    const firstNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [transaction, validated, firstName, changeFirstName])

    const lastNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [validated, email, changeEmail])

    const messageUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.message')} type="text" validated={validated} value={message}
                          fieldName="message"
                          onChange={changeMessage}/>
        </div>
    }, [validated, message, changeMessage])

    const transactionUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.transaction_id')} type="text" validated={validated}
                          value={transaction}
                          fieldName="mb_transaction"
                          onChange={changeTransaction}/>
        </div>
    }, [validated, transaction, changeTransaction])

    const countryUI = useMemo(() => {
        if (!countriesList)
            return <></>;

        return <div className="max-w-md mt-4">
            <DropdownInput
                list={countriesList.data.map((c) => {
                    return {
                        name: c.name, id: c.id, icon: <CountryAvatar country={c as CountryShort} withName={false}
                                                                     className="h-3 max-w-[1rem]"/>
                    }
                })}
                name={t('admin.withdraws.country')} selected={country} searchable={true} onChange={changeCountry}/>
        </div>
    }, [country, changeCountry, countriesList]);

    const currencyUI = useMemo(() => {
        if (!currenciesList)
            return <></>;

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.currency')} className="w-full min-w-[200px]"
                           selected={accountCurrency}
                           onChange={changeCurrency}
                           list={currenciesList}/>
        </div>
    }, [validated, currenciesList, accountCurrency, changeCurrency])


    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        getCurrencyAccount().then(() => {
            getCountries().then(() => {
                if (clientTransactionID) {
                    getClientTransaction();
                }
            })
        })
    }, [broker, clientTransactionID]);

    return <>
        {countryUI}
        {currencyUI}
        {transactionUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {amountUI}
        {messageUI}
        {submitButtonUI}
    </>
}

export default memo(SkrillForm);
